/** @module Sends a few browser events to the server log; a phone has no console we can read. */
type Level = "debug" | "info" | "warning" | "error";
type Detail = Record<string, number | string | boolean | null>;

export interface LogEntry {
  level: Level;
  event: string;
  detail: Detail;
}

/** Where the lines go; the app plugs in its API, the collector plugs in nothing. */
export type LogTransport = (entries: LogEntry[], screen: string | null) => Promise<unknown>;

/** Lines are collected and sent together; one request per event would flood the phone. */
const FLUSH_MS = 3000;
const MAX_ENTRIES = 50;

let pending: LogEntry[] = [];
let transport: LogTransport | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let screen: string | null = null;
let enabled = false;
const RANK: Record<Level, number> = { debug: 0, info: 1, warning: 2, error: 3 };
let minimum: Level = "debug";

export function setLogTransport(send: LogTransport): void {
  transport = send;
}

export function setLogScreen(name: string | null): void {
  screen = name;
}

/**
 * Turn reporting on; off by default so normal use does not chatter.
 *
 * `from` drops everything below it: a phone that reports all the time sends what
 * happened, not eight frame measurements a second.
 */
export function setLogging(on: boolean, from: Level = "debug"): void {
  enabled = on;
  minimum = from;
  if (!on) pending = [];
}

export function isLogging(): boolean {
  return enabled;
}

async function flush(): Promise<void> {
  timer = null;
  const entries = pending;
  pending = [];
  if (entries.length === 0 || !transport) return;
  try {
    await transport(entries, screen);
  } catch {
    // Losing a diagnostic line must never disturb what the person is doing.
  }
}

export function log(event: string, detail: Detail = {}, level: Level = "info"): void {
  // Errors are always worth a line: a tester's broken screen is otherwise never seen.
  if (level !== "error" && (!enabled || RANK[level] < RANK[minimum])) return;
  // The phone's clock at the moment it happened; sending is batched seconds later.
  if (pending.length < MAX_ENTRIES)
    pending.push({ level, event, detail: { ...detail, at: Date.now() } });
  timer ??= setTimeout(() => void flush(), FLUSH_MS);
}
